import { useState, useEffect, ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, Upload, Loader, ChevronUp, ChevronDown } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { uploadToCloudinary } from '../../config/cloudinary';
import { Project, ProjectFormData } from '../../types/types';
import ArrayInput from './ArrayInput';

const ProjectsManager = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<ProjectFormData>({
    projectName: '',
    objective: '',
    coverPhoto: '',
    detailedDescription: '',
    techStack: [],
    githubLink: '',
    liveDemoLink: '',
    tutorialLink: '',
    date: '',
    level: 'Medium',
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'projects'));
      const projectsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Project[];
      const levelOrder: Record<string, number> = { 'Superior': 5, 'Advanced': 4, 'Medium': 3, 'Basic': 2, 'Minor': 1 };
      const sorted = projectsData.sort((a, b) => {
        const levelDiff = (levelOrder[b.level] || 0) - (levelOrder[a.level] || 0);
        if (levelDiff !== 0) return levelDiff;
        
        const aOrder = a.order !== undefined ? a.order : -1;
        const bOrder = b.order !== undefined ? b.order : -1;
        if (bOrder !== aOrder) {
          return bOrder - aOrder;
        }

        if (a.date && b.date) {
          return b.date.localeCompare(a.date);
        }
        return 0;
      });
      setProjects(sorted);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadToCloudinary(file, (progress) => {
        setUploadProgress(progress);
      });
      setFormData({ ...formData, coverPhoto: result.secure_url });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await updateDoc(doc(db, 'projects', editingId), {
          ...formData,
          updatedAt: new Date(),
        });
      } else {
        await addDoc(collection(db, 'projects'), {
          ...formData,
          order: projects.length,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }
      resetForm();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project');
    }
  };

  const handleEdit = (project: Project) => {
    setFormData({
      projectName: project.projectName,
      objective: project.objective,
      coverPhoto: project.coverPhoto,
      detailedDescription: project.detailedDescription,
      techStack: project.techStack,
      githubLink: project.githubLink,
      liveDemoLink: project.liveDemoLink,
      tutorialLink: project.tutorialLink || '',
      date: project.date || '',
      level: project.level || 'Medium',
    });
    setEditingId(project.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await deleteDoc(doc(db, 'projects', id));
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const resetForm = () => {
    setFormData({
      projectName: '',
      objective: '',
      coverPhoto: '',
      detailedDescription: '',
      techStack: [],
      githubLink: '',
      liveDemoLink: '',
      tutorialLink: '',
      date: '',
      level: 'Medium',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === projects.length - 1)
    ) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Validate we are swapping within the exact same level
    if (projects[index].level !== projects[swapIndex].level) {
      return; 
    }

    const newProjects = [...projects];

    // Swap strictly visually for optimistic UI update
    const temp = newProjects[index];
    newProjects[index] = newProjects[swapIndex];
    newProjects[swapIndex] = temp;
    
    // We only need to fix the explicit order of the two swapped nodes.
    // However, it's safer to ensure all items in THIS specific level grouping have sequential orders.
    const currentLevel = projects[index].level;
    
    // Filter out only projects of the current level to rebuild their specific orders
    const levelProjects = newProjects.filter(p => p.level === currentLevel);
    
    // Assign new descending orders for just this level's items
    levelProjects.forEach((p, i) => {
      p.order = levelProjects.length - 1 - i;
    });

    setProjects([...newProjects]);

    try {
      const batch = writeBatch(db);
      
      // We'll just batch write the newly calculated orders for the two items that swapped
      // Since they are in the same level, swapping them and recalculating the level's orders
      // is guaranteed to only mathematically change the orders of these two items.
      const item1Ref = doc(db, 'projects', newProjects[index].id);
      batch.update(item1Ref, { order: newProjects[index].order });

      const item2Ref = doc(db, 'projects', newProjects[swapIndex].id);
      batch.update(item2Ref, { order: newProjects[swapIndex].order });

      await batch.commit();
    } catch (error) {
      console.error('Error reordering projects:', error);
      alert('Failed to save new order');
      fetchProjects(); // Revert on failure
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
          Projects Management
        </h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
        >
          <Plus className="w-5 h-5" />
          <span>Add Project</span>
        </motion.button>
      </div>

      {/* Project Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={resetForm}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingId ? 'Edit Project' : 'Add New Project'}
                </h3>
                <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={formData.projectName}
                    onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Objective (One-liner)
                  </label>
                  <input
                    type="text"
                    value={formData.objective}
                    onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                    placeholder="Brief description of the project goal"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cover Photo
                  </label>
                  {formData.coverPhoto && (
                    <img
                      src={formData.coverPhoto}
                      alt="Cover"
                      className="w-full h-48 object-cover rounded-lg mb-2"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="cover-photo"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="cover-photo"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg cursor-pointer"
                  >
                    <Upload className="w-5 h-5" />
                    <span>{uploading ? `Uploading ${uploadProgress}%` : 'Upload Cover Photo'}</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Detailed Description
                  </label>
                  <textarea
                    value={formData.detailedDescription}
                    onChange={(e) => setFormData({ ...formData, detailedDescription: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <ArrayInput
                  label="Tech Stack"
                  values={formData.techStack}
                  onChange={(techStack) => setFormData({ ...formData, techStack })}
                  placeholder="e.g., React, Node.js, MongoDB"
                  buttonText="Add Technology"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Date
                    </label>
                    <input
                      type="month"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Level
                    </label>
                    <select
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value as 'Minor' | 'Basic' | 'Medium' | 'Advanced' | 'Superior' })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="Superior">Superior</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Medium">Medium</option>
                      <option value="Basic">Basic</option>
                      <option value="Minor">Minor</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      GitHub Link
                    </label>
                    <input
                      type="url"
                      value={formData.githubLink}
                      onChange={(e) => setFormData({ ...formData, githubLink: e.target.value })}
                      placeholder="https://github.com/..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Live Demo Link
                    </label>
                    <input
                      type="url"
                      value={formData.liveDemoLink}
                      onChange={(e) => setFormData({ ...formData, liveDemoLink: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tutorial Link
                  </label>
                  <input
                    type="url"
                    value={formData.tutorialLink}
                    onChange={(e) => setFormData({ ...formData, tutorialLink: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleSubmit}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    <Save className="w-5 h-5" />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Projects List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden relative group"
          >
            <div className="absolute right-4 top-4 flex flex-col space-y-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10 bg-white/80 dark:bg-gray-800/80 rounded shadow backdrop-blur-sm p-1">
              <button
                onClick={() => handleMove(index, 'up')}
                // Disabled if it's the very first item OR if the item above it is of a different level
                disabled={index === 0 || projects[index - 1].level !== project.level}
                className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-30 disabled:hover:text-gray-500"
                title="Move up (within category)"
              >
                <ChevronUp className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleMove(index, 'down')}
                // Disabled if it's the very last item OR if the item below it is of a different level
                disabled={index === projects.length - 1 || projects[index + 1].level !== project.level}
                className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-30 disabled:hover:text-gray-500"
                title="Move down (within category)"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
            {project.coverPhoto && (
              <img
                src={project.coverPhoto}
                alt={project.projectName}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                {project.projectName}
              </h3>
              <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">
                {project.objective}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                {project.detailedDescription}
              </p>

              <div className="flex flex-wrap gap-1 mb-3">
                {project.techStack.slice(0, 3).map((tech, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                  >
                    {tech}
                  </span>
                ))}
                {project.techStack.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                    +{project.techStack.length - 3}
                  </span>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(project)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No projects yet. Click "Add Project" to get started.
        </div>
      )}
    </div>
  );
};

export default ProjectsManager;
